import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { notification } from 'antd';
import { FiArrowRight, FiCheck } from 'react-icons/fi';
import PropertyService from 'service/PropertyService';
import BookingService from 'service/BookingService';
import PricingService from 'service/PricingService';
import { transformBookingSettingsFromApi } from 'library/helpers/bookingSettingsFromApi';
import { clearImportEdit } from 'library/helpers/importEditFlow';
import MissingItemModal, { MissingItemKey } from './MissingItemModal';
import {
  isFlexibleCheckinAmenityName,
  isParkingAmenityName,
  isPoolAmenityName,
} from 'library/helpers/amenityKinds';
import { HOME_PAGE } from 'settings/constant';
import { isAdminUser } from 'library/utils/isAdmin';
import { OnboardingShell, Field, Grid, TimePicker, Spinner, ob } from 'components/Onboarding';

type Parking = 'private' | 'street' | '';

interface Props {
  propertyId: number | string;
  rail: readonly string[];
  onBack: () => void;
}

/** One thing the import couldn't fill in, with where the host can add it. */
interface Item {
  key: string;
  title: string;
  /** What is saved now (when `done`), or a short hint on what to add. */
  note: string;
  done: boolean;
  /** Editor page that edits it; the host returns to this screen afterwards. */
  path: string;
}

const PARKING_TEXT: Record<'private' | 'street', string> = {
  private: 'Private parking',
  street: 'Street parking',
};

const countImages = (list: Record<string, string[]> | undefined): number =>
  Object.values(list || {}).reduce((sum, urls) => sum + (urls?.length || 0), 0);

/** How many photos the listing had the first time this screen opened (what the import brought over). */
const photoBaseline = (propertyId: number | string, current: number): number => {
  const key = `missingPhotosBaseline_${propertyId}`;
  try {
    const saved = sessionStorage.getItem(key);
    if (saved !== null && Number.isFinite(Number(saved))) return Number(saved);
    sessionStorage.setItem(key, String(current));
  } catch (_) {
    /* storage unavailable */
  }
  return current;
};

/** Items the host has opened an editor for (and come back from), so they show as updated. */
const visitedKey = (id: number | string): string => `missingVisited_${id}`;
const readVisited = (id: number | string): string[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(visitedKey(id)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};
const markVisited = (id: number | string, key: string): void => {
  try {
    const next = Array.from(new Set([...readVisited(id), key]));
    sessionStorage.setItem(visitedKey(id), JSON.stringify(next));
  } catch (_) {
    /* storage unavailable */
  }
};

/** What the Amenities page remembers locally after a save (the server may not return it back). */
const readAmenityCache = (id: number | string): Record<string, any> => {
  try {
    return JSON.parse(localStorage.getItem(`amenityFollowUp_${id}`) || '{}') || {};
  } catch (_) {
    return {};
  }
};

const FLEX_TYPES = ['self', 'assisted', 'front_desk', 'digital'];

/** Items whose saved value can't always be read back, so a visit to their editor counts as an update. */
const FALLBACK_KEYS = ['pool', 'flex'];

const prettyDays = (raw: string): string =>
  raw
    .split(/[,\s]+/)
    .filter(Boolean)
    .map((d) => d.charAt(0).toUpperCase() + d.slice(1).toLowerCase())
    .join(', ');

/** "What did we miss?": what the import couldn't fill in and is still blank; filled items drop off. */
const MissingDetails: React.FC<Props> = ({ propertyId, rail, onBack }) => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parking, setParking] = useState<Parking>('');
  const [checkIn, setCheckIn] = useState('15:00');
  const [checkOut, setCheckOut] = useState('11:00');
  const [booking, setBooking] = useState<Record<string, any> | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  /** The optional detail being added / edited in the popup, if any. */
  const [editing, setEditing] = useState<Item | null>(null);
  /** Bumped after a popup saves, to re-read what is saved. */
  const [reloadKey, setReloadKey] = useState(0);

  // Back on this page: any edit return target has been used up.
  useEffect(() => {
    clearImportEdit();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [propRes, settingsRes, photoRes, videoRes, pricingRes]: any[] = await Promise.all([
          PropertyService.getPropertyDetailsById(propertyId),
          PropertyService.getBookingSettingsByPropertyId(propertyId),
          PropertyService.getPhotos(propertyId).catch(() => null),
          PropertyService.getPropertyVideo(propertyId).catch(() => null),
          PricingService.getPropertyPricingDetails(propertyId).catch(() => null),
        ]);
        if (cancelled) return;

        const server = propRes?.result || {};
        const meta = server.propertyMetadata || {};
        const amenities: any[] = server.amenities || [];
        const names = amenities.map((a) => String(a?.name || ''));

        // Parking: the Parking amenity's note says which kind it is.
        const parkingAmenity = amenities.find((a) => isParkingAmenityName(String(a?.name || '')));
        const note = String(parkingAmenity?.description || meta.parkingDescription || '');
        if (/private/i.test(note)) setParking('private');
        else if (/street/i.test(note)) setParking('street');

        const settings = settingsRes?.result ? transformBookingSettingsFromApi(settingsRes.result) : null;
        if (settings) {
          setBooking(settings);
          if (settings.checkInTime) setCheckIn(String(settings.checkInTime).slice(0, 5));
          if (settings.checkOutTime) setCheckOut(String(settings.checkOutTime).slice(0, 5));
        }

        const id = propertyId;
        const next: Item[] = [];

        // Details that depend on the amenities the listing has.
        const cache = readAmenityCache(id);
        if (names.some(isPoolAmenityName)) {
          // Saved as the pool amenity's note ("Length: 10, Width: 4, Depth: 2") and in the metadata.
          const poolNote = String(
            amenities.find((a) => isPoolAmenityName(String(a?.name || '')))?.description || '',
          );
          const dim = (label: string): string =>
            (poolNote.match(new RegExp(`${label}:\\s*([^,]+)`, 'i'))?.[1] || '').trim();
          const poolLength = meta.poolLength || cache.poolLength || dim('Length');
          const poolBreadth = meta.poolBreadth || cache.poolBreadth || dim('Width');
          const poolDepth = meta.poolDepth || cache.poolDepth || dim('Depth');
          const hasPool = !!(poolLength && poolBreadth && poolDepth);
          next.push({
            key: 'pool',
            title: 'Pool size',
            note: hasPool
              ? `${poolLength} × ${poolBreadth} × ${poolDepth}`
              : 'Length, breadth and depth of the pool',
            done: hasPool,
            path: `/host/amenities?id=${id}`,
          });
        }
        if (names.some(isFlexibleCheckinAmenityName)) {
          const flexNote = String(
            amenities.find((a) => isFlexibleCheckinAmenityName(String(a?.name || '')))?.description || '',
          )
            .toLowerCase()
            .replace(/[-\s]+/g, '_');
          const type = String(
            meta.flexibleCheckinType ||
              cache.flexibleCheckinType ||
              (FLEX_TYPES.includes(flexNote) ? flexNote : ''),
          );
          next.push({
            key: 'flex',
            title: 'Flexible check-in / check-out',
            note: type ? type.replace(/_/g, ' ') : 'How guests check in outside the usual hours',
            done: !!type,
            path: `/host/amenities?id=${id}`,
          });
        }

        // Photos and videos.
        const images = countImages(photoRes?.result?.imageUrlList);
        const importedImages = photoBaseline(propertyId, images);
        next.push({
          key: 'photos',
          title: 'More photos',
          note: images
            ? `${images} photo${images === 1 ? '' : 's'} imported — add more if you have them`
            : 'No photos imported yet',
          done: images >= 10 || images > importedImages,
          path: `/host/photo-upload?id=${id}`,
        });
        const videoOk = videoRes?.responseInformation?.responseCode === 0;
        const mainVideo = videoOk ? videoRes?.result?.videoUrl : null;
        const walkthrough = videoOk ? videoRes?.result?.walkthroughVideoUrl : null;
        next.push({
          key: 'video',
          title: 'Property video',
          note: mainVideo ? 'Added' : 'A short video of the property',
          done: !!mainVideo,
          path: `/host/photo-upload?id=${id}`,
        });
        next.push({
          key: 'walkthrough',
          title: 'Walkthrough video',
          note: walkthrough ? 'Added' : 'A room-by-room walkthrough',
          done: !!walkthrough,
          path: `/host/photo-upload?id=${id}`,
        });

        // Pricing.
        const pricing = pricingRes?.result || {};
        const weekendDays = String(pricing.weekendDays || '').trim();
        next.push({
          key: 'weekendDays',
          title: 'Weekend days',
          note: weekendDays ? prettyDays(weekendDays) : 'Which nights count as the weekend',
          done: !!weekendDays,
          path: `/host/property-listing?id=${id}`,
        });
        next.push({
          key: 'weekendPrice',
          title: 'Weekend price',
          note: pricing.weekendDefaultPrice
            ? `₹${pricing.weekendDefaultPrice} per night`
            : 'Your nightly price for weekend nights',
          done: !!pricing.weekendDefaultPrice,
          path: `/host/property-listing?id=${id}`,
        });

        // The refundable security deposit lives on the same pricing page as the weekend settings.
        const deposit = Number(pricing.securityDeposit) || 0;
        next.push({
          key: 'refundable',
          title: 'Refundable amount',
          note: deposit
            ? `₹${deposit} refundable security deposit`
            : 'The security deposit guests get back after the stay',
          done: deposit > 0,
          path: `/host/property-listing?id=${id}`,
        });

        // Booking terms.
        const rules: any[] = settings?.restrictions || [];
        next.push({
          key: 'rules',
          title: 'House rules',
          note: rules.length
            ? `${rules.length} rule${rules.length === 1 ? '' : 's'} added`
            : 'Smoking, pets, parties, quiet hours…',
          done: rules.length > 0,
          path: `/host/house-rules?id=${id}&edit=1`,
        });

        const visited = readVisited(propertyId);
        // Pool size and flexible check-in are only known from local notes, so opening their editor
        // counts as updating them; everything else is read from what is actually saved.
        const merged = next.map((item) =>
          !item.done && FALLBACK_KEYS.includes(item.key) && visited.includes(item.key)
            ? { ...item, done: true, note: 'Updated' }
            : item,
        );
        setItems([...merged.filter((i) => !i.done), ...merged.filter((i) => i.done)]);
      } catch (_) {
        // the defaults stay; the host can still answer the quick questions
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, reloadKey]);

  const pending = [
    !parking && 'Tell us about parking.',
    (!checkIn || !checkOut) && 'Set the arrival and departure times.',
  ].filter((item): item is string => typeof item === 'string');

  /** Opens a popup to add / update an item without leaving this screen. */
  const openEditor = (item: Item): void => setEditing(item);

  const save = async (): Promise<void> => {
    if (!parking || saving) return;
    setSaving(true);
    try {
      // Parking: stored as the Parking amenity's note plus the property metadata, like the editor does.
      const [propRes, amenityList]: any[] = await Promise.all([
        PropertyService.getPropertyDetailsById(propertyId),
        PropertyService.getAmenities(),
      ]);
      const server = propRes?.result;
      if (!server) throw new Error('Property not found');
      // Saving booking settings sets the listing's stage; keep it where it is if it is already past 4
      // (e.g. 5 = Pending Approval), otherwise this step would pull it back out of that tab.
      const stage = Math.max(Number(server.onboardingStage) || 0, 4);
      const text = PARKING_TEXT[parking];
      const amenities: any[] = [...(server.amenities || [])];
      const at = amenities.findIndex((a) => isParkingAmenityName(String(a?.name || '')));
      if (at >= 0) {
        amenities[at] = { ...amenities[at], description: text };
      } else {
        const def = (amenityList?.result || []).find((a: any) => isParkingAmenityName(String(a.name)));
        if (def) amenities.push({ id: def.id, name: def.name, description: text });
      }
      await PropertyService.updateProperty({
        ...server,
        propertyId,
        amenities,
        propertyMetadata: { ...(server.propertyMetadata || {}), parkingDescription: text },
      });

      // Times: re-save the booking settings the import created, changing only the two times.
      const base = booking || {};
      const res: any = await BookingService.savePropertyBookingSettings({
        propertyId,
        checkInTime: checkIn,
        checkOutTime: checkOut,
        cancellationPolicy: JSON.stringify(base.cancellationPolicy),
        propertyPolicy: JSON.stringify(base.restrictions || []),
        partialPaymentAllowed: base.partialPaymentAllowed ?? true,
        partialPaymentPercentage: base.partialPaymentPercentage ?? 50,
        onboardingStage: stage,
        approvalRequiredForBooking: false,
      });
      if (res?.responseInformation?.responseCode !== 0) throw new Error('Booking settings not saved');

      localStorage.setItem('newPropertId', String(propertyId));
      // Admins skip the plan screen: their listings are plan-free.
      navigate(
        isAdminUser()
          ? `/host/acknowledgement?id=${propertyId}&flow=import`
          : `/host/hosting-plan?id=${propertyId}&flow=import`,
      );
    } catch (_) {
      notification.error({
        message: 'Couldn’t save your answers',
        description: 'Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const open = items.filter((i) => !i.done);

  return (
    <OnboardingShell
      section={1}
      railItems={rail}
      title="What did we miss?"
      intro={
        open.length
          ? `We couldn’t fill in ${open.length + (parking ? 0 : 1)} thing${
              open.length + (parking ? 0 : 1) === 1 ? '' : 's'
            } from your existing listing. Add what you like now — you can skip the rest.`
          : 'Everything looks filled in. Confirm the quick answers below and continue.'
      }
      headerBack={() => navigate(HOME_PAGE)}
      onBack={onBack}
      onContinue={save}
      continueLoading={saving}
      continueDisabled={!ready || pending.length > 0}
      pending={pending}
      showImport={false}
    >
      {!ready ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          <Question>
            <h3>Is parking available on the property?</h3>
            <p>Tell guests what to expect when they arrive.</p>
            <Options>
              {(['private', 'street'] as const).map((key) => (
                <Option
                  key={key}
                  type="button"
                  $active={parking === key}
                  aria-pressed={parking === key}
                  onClick={() => setParking(key)}
                >
                  {key === 'private' ? 'Yes, private parking' : 'No, street parking'}
                </Option>
              ))}
            </Options>
          </Question>

          <Question>
            <h3>What time can guests arrive?</h3>
            <p>Set the earliest check-in and the check-out time.</p>
            <Grid $cols={2} $gap={18}>
              <Field label="From">
                <TimePicker ariaLabel="Check-in time" value={checkIn} onChange={setCheckIn} />
              </Field>
              <Field label="Until">
                <TimePicker ariaLabel="Check-out time" value={checkOut} onChange={setCheckOut} />
              </Field>
            </Grid>
          </Question>

          {items.length ? (
            <Question>
              <h3>Optional details</h3>
              <p>Add what you like now. Anything you’ve added shows as Updated.</p>
              <List>
                {items.map((item) => (
                  <Row key={item.key} $done={item.done}>
                    <div className="info">
                      <b>
                        {item.done ? <FiCheck size={15} /> : null}
                        {item.title}
                        {item.done ? <Tag>Updated</Tag> : null}
                      </b>
                      <span>{item.note}</span>
                    </div>
                    {item.done ? (
                      <EditButton type="button" onClick={() => openEditor(item)}>
                        Edit
                      </EditButton>
                    ) : (
                      <AddButton type="button" onClick={() => openEditor(item)}>
                        Add <FiArrowRight size={15} />
                      </AddButton>
                    )}
                  </Row>
                ))}
              </List>
            </Question>
          ) : null}

        </>
      )}
      <MissingItemModal
        itemKey={(editing?.key as MissingItemKey) ?? null}
        title={editing?.title ?? ''}
        propertyId={propertyId}
        onClose={() => setEditing(null)}
        onSaved={(key) => {
          markVisited(propertyId, key);
          setReloadKey((n) => n + 1);
        }}
      />
    </OnboardingShell>
  );
};

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const Question = styled.section`
  h3 {
    margin: 0 0 6px;
    font-size: 19px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  p {
    margin: 0 0 16px;
    font-size: 15.5px;
    color: ${ob.muted};
  }
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const Option = styled.button<{ $active?: boolean }>`
  height: 72px;
  padding: 0 16px;
  border: 1.5px solid ${({ $active }) => ($active ? ob.primary : ob.border)};
  border-radius: 20px;
  background: ${({ $active }) => ($active ? ob.primarySoft : '#fff')};
  font-size: 18px;
  font-weight: 600;
  color: ${ob.text};
  cursor: pointer;
  box-shadow: ${ob.shadowCard};
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${ob.primary};
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div<{ $done?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 18px;
  border: 1px solid ${ob.border};
  border-radius: 16px;
  background: ${({ $done }) => ($done ? ob.surface : '#fff')};
  .info {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }
  b {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 16px;
    font-weight: 600;
    color: ${({ $done }) => ($done ? ob.success : ob.text)};
  }
  span {
    font-size: 14px;
    color: ${ob.muted};
    overflow-wrap: anywhere;
  }
`;

const AddButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid ${ob.primary};
  border-radius: 999px;
  background: ${ob.primarySoft};
  color: ${ob.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: ${ob.primary};
    color: #fff;
  }
`;

const Tag = styled.span`
  padding: 2px 9px;
  border-radius: 999px;
  background: ${ob.successSoft};
  color: ${ob.success};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const EditButton = styled.button`
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${ob.borderStrong};
  border-radius: 999px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: ${ob.primary};
  }
`;

export default MissingDetails;
